import React, { useState } from 'react';
import { ShieldCheckIcon, AlertTriangleIcon, XCircleIcon, CheckCircleIcon, ClockIcon, PlaneIcon, BuildingIcon, ShieldIcon, CarIcon, PackageIcon, EditIcon, InfoIcon, ChevronDownIcon, ChevronRightIcon } from 'lucide-react';
import { useAppContext } from '../context/AppContext';
export function RefundPoliciesPage() {
  const {
    refundPolicies
  } = useAppContext();
  const [expandedPolicy, setExpandedPolicy] = useState<string | null>(null);
  const [activeCategory, setActiveCategory] = useState('all');
  const categories = ['all', 'Flights', 'Hotels', 'Insurance', 'Ground Transport', 'Ancillary'];
  const filteredPolicies = activeCategory === 'all' ? refundPolicies : refundPolicies.filter(p => p.service === activeCategory);
  const getServiceIcon = (service: string) => {
    switch (service) {
      case 'Flights':
        return PlaneIcon;
      case 'Hotels':
        return BuildingIcon;
      case 'Insurance':
        return ShieldIcon;
      case 'Ground Transport':
        return CarIcon;
      case 'Ancillary':
        return PackageIcon;
      default:
        return PackageIcon;
    }
  };
  const getServiceColor = (service: string) => {
    switch (service) {
      case 'Flights':
        return {
          bg: 'bg-blue-50',
          text: 'text-blue-700',
          border: 'border-blue-200'
        };
      case 'Hotels':
        return {
          bg: 'bg-indigo-50',
          text: 'text-indigo-700',
          border: 'border-indigo-200'
        };
      case 'Insurance':
        return {
          bg: 'bg-red-50',
          text: 'text-red-700',
          border: 'border-red-200'
        };
      case 'Ground Transport':
        return {
          bg: 'bg-emerald-50',
          text: 'text-emerald-700',
          border: 'border-emerald-200'
        };
      case 'Ancillary':
        return {
          bg: 'bg-slate-50',
          text: 'text-slate-700',
          border: 'border-slate-200'
        };
      default:
        return {
          bg: 'bg-slate-50',
          text: 'text-slate-700',
          border: 'border-slate-200'
        };
    }
  };
  return <div className="space-y-6" data-id="element-2134">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-2135">
        <div data-id="element-2136">
          <h2 className="text-2xl font-bold text-navy-900" data-id="element-2137">
            Refund & Cancellation Policies
          </h2>
          <p className="text-slate-500" data-id="element-2138">
            Transparent policy rules for every service type. Users must
            acknowledge before booking.
          </p>
        </div>
      </div>

      {/* Policy Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-2139">
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4" data-id="element-2140">
          <div className="flex items-center gap-2 mb-2" data-id="element-2141">
            <CheckCircleIcon className="h-5 w-5 text-emerald-600" data-id="element-2142" />
            <span className="text-sm font-bold text-emerald-800" data-id="element-2143">
              Refundable
            </span>
          </div>
          <p className="text-2xl font-bold text-emerald-900" data-id="element-2144">
            {refundPolicies.filter(p => p.refundable).length}
          </p>
          <p className="text-xs text-emerald-700" data-id="element-2145">
            services with refund options
          </p>
        </div>
        <div className="rounded-xl border border-red-200 bg-red-50 p-4" data-id="element-2146">
          <div className="flex items-center gap-2 mb-2" data-id="element-2147">
            <XCircleIcon className="h-5 w-5 text-red-600" data-id="element-2148" />
            <span className="text-sm font-bold text-red-800" data-id="element-2149">
              Non-Refundable
            </span>
          </div>
          <p className="text-2xl font-bold text-red-900" data-id="element-2150">
            {refundPolicies.filter(p => !p.refundable).length}
          </p>
          <p className="text-xs text-red-700" data-id="element-2151">services with no refund</p>
        </div>
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4" data-id="element-2152">
          <div className="flex items-center gap-2 mb-2" data-id="element-2153">
            <AlertTriangleIcon className="h-5 w-5 text-amber-600" data-id="element-2154" />
            <span className="text-sm font-bold text-amber-800" data-id="element-2155">
              Conditional
            </span>
          </div>
          <p className="text-2xl font-bold text-amber-900" data-id="element-2156">
            {refundPolicies.filter(p => p.refundable && p.penalties !== '').length}
          </p>
          <p className="text-xs text-amber-700" data-id="element-2157">
            services with conditions/penalties
          </p>
        </div>
      </div>

      {/* Category Filter */}
      <div className="flex gap-2 overflow-x-auto pb-2" data-id="element-2158">
        {categories.map(cat => <button key={cat} onClick={() => setActiveCategory(cat)} className={`rounded-full px-4 py-1.5 text-sm font-medium whitespace-nowrap transition-colors ${activeCategory === cat ? 'bg-navy-900 text-white' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'}`} data-id="element-2159">
            {cat === 'all' ? 'All Services' : cat}
          </button>)}
      </div>

      {/* Policy Cards */}
      <div className="space-y-4" data-id="element-2160">
        {filteredPolicies.map(policy => {
        const ServiceIcon = getServiceIcon(policy.service);
        const colors = getServiceColor(policy.service);
        const isExpanded = expandedPolicy === policy.id;
        return <div key={policy.id} className={`rounded-xl border bg-white shadow-sm overflow-hidden ${colors.border}`} data-id="element-2161">
              <button onClick={() => setExpandedPolicy(isExpanded ? null : policy.id)} className="w-full p-5 flex items-center justify-between text-left" data-id="element-2162">
                <div className="flex items-center gap-4" data-id="element-2163">
                  <div className={`h-12 w-12 rounded-xl ${colors.bg} flex items-center justify-center`} data-id="element-2164">
                    <ServiceIcon className={`h-6 w-6 ${colors.text}`} data-id="element-2165" />
                  </div>
                  <div data-id="element-2166">
                    <h3 className="font-bold text-slate-900" data-id="element-2167">
                      {policy.service}
                    </h3>
                    <div className="flex items-center gap-2 mt-1" data-id="element-2168">
                      {policy.refundable ? <span className="flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full" data-id="element-2169">
                          <CheckCircleIcon className="h-3 w-3" data-id="element-2170" /> Refundable
                        </span> : <span className="flex items-center gap-1 text-xs font-semibold text-red-700 bg-red-100 px-2 py-0.5 rounded-full" data-id="element-2171">
                          <XCircleIcon className="h-3 w-3" data-id="element-2172" /> Non-Refundable
                        </span>}
                      <span className="flex items-center gap-1 text-xs text-slate-500" data-id="element-2173">
                        <ClockIcon className="h-3 w-3" data-id="element-2174" /> {policy.timeLimit}
                      </span>
                    </div>
                  </div>
                </div>
                {isExpanded ? <ChevronDownIcon className="h-5 w-5 text-slate-400" data-id="element-2175" /> : <ChevronRightIcon className="h-5 w-5 text-slate-400" data-id="element-2176" />}
              </button>

              {isExpanded && <div className="border-t border-slate-100 p-5 space-y-4" data-id="element-2177">
                  {/* Cancellation Timeline */}
                  <div data-id="element-2178">
                    <h4 className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-1.5" data-id="element-2179">
                      <ClockIcon className="h-4 w-4" data-id="element-2180" /> Cancellation Timeline
                    </h4>
                    <div className="relative pl-6" data-id="element-2181">
                      <div className="absolute left-2 top-0 bottom-0 w-0.5 bg-slate-200" data-id="element-2182" />
                      <div className="space-y-4" data-id="element-2183">
                        <div className="relative" data-id="element-2184">
                          <div className="absolute -left-4 top-1 h-3 w-3 rounded-full bg-emerald-500 ring-2 ring-white" data-id="element-2185" />
                          <div className="bg-emerald-50 rounded-lg p-3 border border-emerald-200" data-id="element-2186">
                            <p className="text-sm font-medium text-emerald-800" data-id="element-2187">
                              Free Cancellation Window
                            </p>
                            <p className="text-xs text-emerald-600" data-id="element-2188">
                              {policy.timeLimit}
                            </p>
                          </div>
                        </div>
                        {policy.refundable && <div className="relative" data-id="element-2189">
                            <div className="absolute -left-4 top-1 h-3 w-3 rounded-full bg-amber-500 ring-2 ring-white" data-id="element-2190" />
                            <div className="bg-amber-50 rounded-lg p-3 border border-amber-200" data-id="element-2191">
                              <p className="text-sm font-medium text-amber-800" data-id="element-2192">
                                Penalty Period
                              </p>
                              <p className="text-xs text-amber-600" data-id="element-2193">
                                {policy.penalties}
                              </p>
                            </div>
                          </div>}
                        <div className="relative" data-id="element-2194">
                          <div className="absolute -left-4 top-1 h-3 w-3 rounded-full bg-red-500 ring-2 ring-white" data-id="element-2195" />
                          <div className="bg-red-50 rounded-lg p-3 border border-red-200" data-id="element-2196">
                            <p className="text-sm font-medium text-red-800" data-id="element-2197">
                              No Refund
                            </p>
                            <p className="text-xs text-red-600" data-id="element-2198">
                              After cancellation window closes
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Details Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-2199">
                    <div className="bg-slate-50 rounded-lg p-4" data-id="element-2200">
                      <h5 className="text-xs font-bold text-slate-500 uppercase mb-2" data-id="element-2201">
                        Cancellation Rules
                      </h5>
                      <p className="text-sm text-slate-700" data-id="element-2202">
                        {policy.cancellationRules}
                      </p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-4" data-id="element-2203">
                      <h5 className="text-xs font-bold text-slate-500 uppercase mb-2" data-id="element-2204">
                        Penalties
                      </h5>
                      <p className="text-sm text-slate-700" data-id="element-2205">
                        {policy.penalties}
                      </p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-4 md:col-span-2" data-id="element-2206">
                      <h5 className="text-xs font-bold text-slate-500 uppercase mb-2" data-id="element-2207">
                        Conditions
                      </h5>
                      <p className="text-sm text-slate-700" data-id="element-2208">
                        {policy.conditions}
                      </p>
                    </div>
                  </div>

                  {/* Policy Acknowledgment Note */}
                  <div className="flex items-start gap-3 p-4 bg-navy-50 rounded-lg border border-navy-200" data-id="element-2209">
                    <InfoIcon className="h-5 w-5 text-navy-600 flex-shrink-0 mt-0.5" data-id="element-2210" />
                    <div data-id="element-2211">
                      <p className="text-sm font-medium text-navy-800" data-id="element-2212">
                        Policy Acknowledgment Required
                      </p>
                      <p className="text-xs text-navy-600 mt-0.5" data-id="element-2213">
                        Users must acknowledge and accept this policy before
                        completing their booking.
                      </p>
                    </div>
                  </div>
                </div>}
            </div>;
      })}
      </div>

      {/* Policy Comparison Table */}
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden" data-id="element-2214">
        <div className="border-b border-slate-200 px-6 py-4" data-id="element-2215">
          <h3 className="font-semibold text-slate-800" data-id="element-2216">Policy Comparison</h3>
        </div>
        <div className="overflow-x-auto" data-id="element-2217">
          <table className="w-full text-left text-sm" data-id="element-2218">
            <thead className="bg-slate-50 text-slate-500" data-id="element-2219">
              <tr data-id="element-2220">
                <th className="px-6 py-3 font-medium" data-id="element-2221">Service</th>
                <th className="px-6 py-3 font-medium" data-id="element-2222">Refundable</th>
                <th className="px-6 py-3 font-medium" data-id="element-2223">Time Limit</th>
                <th className="px-6 py-3 font-medium" data-id="element-2224">Penalty</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-2225">
              {refundPolicies.map(policy => <tr key={policy.id} className="hover:bg-slate-50" data-id="element-2226">
                  <td className="px-6 py-4 font-medium text-slate-900" data-id="element-2227">
                    {policy.service}
                  </td>
                  <td className="px-6 py-4" data-id="element-2228">
                    {policy.refundable ? <span className="flex items-center gap-1 text-xs font-semibold text-emerald-700" data-id="element-2229">
                        <CheckCircleIcon className="h-3.5 w-3.5" data-id="element-2230" /> Yes
                      </span> : <span className="flex items-center gap-1 text-xs font-semibold text-red-700" data-id="element-2231">
                        <XCircleIcon className="h-3.5 w-3.5" data-id="element-2232" /> No
                      </span>}
                  </td>
                  <td className="px-6 py-4 text-slate-600" data-id="element-2233">
                    {policy.timeLimit}
                  </td>
                  <td className="px-6 py-4 text-slate-600 text-xs" data-id="element-2234">
                    {policy.penalties.substring(0, 60)}...
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
}